import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, TextField, MenuItem, CircularProgress,
  Alert, InputAdornment, IconButton, Divider, Typography, Box,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import axios from 'axios';

const TIPOS_DOC = [
  { codigo: '6', descripcion: 'RUC' },
  { codigo: '1', descripcion: 'DNI' },
  { codigo: '4', descripcion: 'Carnet Extranjería' },
  { codigo: '7', descripcion: 'Pasaporte' },
  { codigo: '0', descripcion: 'Otros' },
];

// Ubigeo por defecto: Lima, Lima, Lima (150101)
const UBIGEO_LIMA = { codigo: '150101', departamento: 'LIMA', provincia: 'LIMA', distrito: 'LIMA' };
const ubigeoLabel = (u) => u ? `${u.departamento} / ${u.provincia} / ${u.distrito} (${u.codigo})` : '';

const buildInitialForm = (query = '', tipoDoc = '6') => ({
  tipo_documento_identidad_id: tipoDoc,
  numero_documento: query,
  razon_social: '',
  nombres: '',
  apellidos: '',
  nombre_comercial: '',
  direccion: '',
  ubigeo: '',
  telefono: '',
  email: '',
  condicion_pago: '',
  tipo: 'CLIENTE',
  activo: 1,
});

/**
 * ClienteRapidoDialog
 * Props:
 *   open            {boolean}  — controla visibilidad
 *   onClose         {function} — cierra el dialog
 *   onClienteCreado {function} — callback(cliente) con el objeto creado
 *   initialQuery    {string}   — texto que el usuario digitó (RUC/nombre)
 */
export default function ClienteRapidoDialog({ open, onClose, onClienteCreado, initialQuery = '', tipo: tipoAux = 'CLIENTE' }) {
  const [form, setForm]               = useState(buildInitialForm());
  const [loading, setLoading]         = useState(false);
  const [consultando, setConsultando] = useState(false);
  const [error, setError]             = useState('');
  const [infoConsulta, setInfoConsulta] = useState('');

  // Ubigeo autocomplete
  const [ubigeoOpciones, setUbigeoOpciones]       = useState([]);
  const [ubigeoSeleccionado, setUbigeoSeleccionado] = useState(null);
  const [ubigeoInput, setUbigeoInput]               = useState('');

  // Condiciones de pago
  const [condicionesPago, setCondicionesPago] = useState([]);
  const [condSeleccionada, setCondSeleccionada] = useState(null);

  // Carga condiciones de pago al montar
  useEffect(() => {
    axios.get('/api/condicion-pago')
      .then(r => setCondicionesPago(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCondicionesPago([]));
  }, []);

  // Busca y selecciona el ubigeo por defecto (Lima 150101)
  const seleccionarUbigeoLima = useCallback(async () => {
    try {
      const res = await axios.get('/api/ubigeo/150101');
      const u = res.data || UBIGEO_LIMA;
      setUbigeoSeleccionado(u);
      setUbigeoInput(ubigeoLabel(u));
      setForm(f => ({ ...f, ubigeo: u.codigo }));
    } catch {
      setUbigeoSeleccionado(UBIGEO_LIMA);
      setUbigeoInput(ubigeoLabel(UBIGEO_LIMA));
      setForm(f => ({ ...f, ubigeo: '150101' }));
    }
  }, []);

  // Selecciona condición CONTADO automáticamente
  const seleccionarContado = useCallback((lista) => {
    const contado = lista.find(c =>
      String(c.descripcion || '').toLowerCase().includes('contado') ||
      String(c.codigo || '').toLowerCase() === 'contado'
    ) || lista[0] || null;
    if (contado) {
      setCondSeleccionada(contado);
      setForm(f => ({ ...f, condicion_pago: contado.id }));
    }
  }, []);

  useEffect(() => {
    if (open) {
      const query         = (initialQuery || '').trim();
      const tipoDetectado = /^\d{11}$/.test(query) ? '6'
                          : /^\d{8}$/.test(query)  ? '1'
                          : '6';
      setForm(buildInitialForm(query, tipoDetectado));
      setError('');
      setInfoConsulta('');
      setUbigeoSeleccionado(null);
      setUbigeoInput('');
      setUbigeoOpciones([]);
      // Ubigeo Lima por defecto (para DNI, RUC-10, o cuando abre)
      seleccionarUbigeoLima();
      // Condición de pago: contado por defecto
      axios.get('/api/condicion-pago')
        .then(r => {
          const lista = Array.isArray(r.data) ? r.data : [];
          setCondicionesPago(lista);
          seleccionarContado(lista);
        })
        .catch(() => {});
      if (/^\d{11}$/.test(query) || /^\d{8}$/.test(query)) {
        setTimeout(() => consultarDocumento(query, tipoDetectado), 300);
      }
    }
  }, [open]); // eslint-disable-line

  const consultarDocumento = async (numero, tipo) => {
    const num = numero ?? form.numero_documento;
    const tip = tipo   ?? form.tipo_documento_identidad_id;
    if (!num) return;
    setConsultando(true);
    setInfoConsulta('');
    setError('');
    try {
      if (tip === '6' && num.length === 11) {
        const res = await axios.get('/api/consulta-peru/ruc/' + num);
        if (res.data?.razonSocial) {
          setForm(f => ({
            ...f,
            razon_social:     res.data.razonSocial     || '',
            nombre_comercial: res.data.nombreComercial || res.data.razonSocial || '',
            direccion:        res.data.direccion       || res.data.domicilioFiscal || '-',
          }));
          // Para RUC-10 (persona natural) usar Lima por defecto; RUC-20 ya viene dirección de SUNAT pero sin ubigeo
          if (num.startsWith('10')) {
            // Persona natural: Lima por defecto
            await seleccionarUbigeoLima();
          } else {
            // Empresa: si SUNAT trae ubigeo lo cargamos, si no Lima por defecto
            const ubigeoSunat = res.data.ubigeo || res.data.codigoUbigeo;
            if (ubigeoSunat) {
              try {
                const ures = await axios.get(`/api/ubigeo/${ubigeoSunat}`);
                const u = ures.data;
                setUbigeoSeleccionado(u);
                setUbigeoInput(ubigeoLabel(u));
                setForm(f => ({ ...f, ubigeo: u.codigo }));
              } catch {
                await seleccionarUbigeoLima();
              }
            } else {
              await seleccionarUbigeoLima();
            }
          }
          setInfoConsulta('✓ Datos obtenidos de SUNAT');
        } else {
          await seleccionarUbigeoLima();
          setInfoConsulta('No se encontró en SUNAT. Ingresa los datos manualmente.');
        }
      } else if (tip === '1' && num.length === 8) {
        const res = await axios.get('/api/consulta-peru/dni/' + num);
        if (res.data?.nombres || res.data?.apellidoPaterno) {
          const razon = ((res.data.apellidoPaterno || '') + ' ' + (res.data.apellidoMaterno || '') + ', ' + (res.data.nombres || '')).trim();
          setForm(f => ({
            ...f,
            nombres:      res.data.nombres || '',
            apellidos:    ((res.data.apellidoPaterno || '') + ' ' + (res.data.apellidoMaterno || '')).trim(),
            razon_social: razon,
            direccion:    '-',
          }));
          // DNI no tiene ubigeo en RENIEC → Lima por defecto
          await seleccionarUbigeoLima();
          setInfoConsulta('✓ Datos obtenidos de RENIEC');
        } else {
          await seleccionarUbigeoLima();
          setInfoConsulta('No se encontró en RENIEC. Ingresa los datos manualmente.');
        }
      } else {
        setInfoConsulta('Ingresa 11 dígitos (RUC) u 8 dígitos (DNI) para consultar.');
      }
    } catch {
      setInfoConsulta('No se pudo consultar. Ingresa los datos manualmente.');
    }
    setConsultando(false);
  };

  // Busca ubigeos al tipear en el autocomplete
  const handleUbigeoInput = async (e, value) => {
    setUbigeoInput(value);
    if (value && value.length >= 2) {
      try {
        const res = await axios.get(`/api/ubigeo?q=${encodeURIComponent(value)}`);
        setUbigeoOpciones(Array.isArray(res.data) ? res.data : []);
      } catch {
        setUbigeoOpciones([]);
      }
    } else {
      setUbigeoOpciones([]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    if (name === 'numero_documento') {
      if (form.tipo_documento_identidad_id === '6' && value.length === 11)
        consultarDocumento(value, '6');
      else if (form.tipo_documento_identidad_id === '1' && value.length === 8)
        consultarDocumento(value, '1');
    }
    if (name === 'tipo_documento_identidad_id') setInfoConsulta('');
  };

  const handleGuardar = async () => {
    if (!form.numero_documento?.trim()) { setError('El número de documento es obligatorio.'); return; }
    if (!form.razon_social?.trim() && !form.nombres?.trim()) { setError('La razón social o nombre es obligatorio.'); return; }
    if (!form.ubigeo?.trim()) { setError('El ubigeo (departamento / provincia / distrito) es obligatorio.'); return; }
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        razon_social: form.razon_social?.trim() || (form.nombres + ' ' + form.apellidos).trim(),
        direccion: form.direccion?.trim() || '-',
        tipo: tipoAux,
        activo: 1,
        estado: 'ACTIVO',
      };
      const res = await axios.post('/api/auxiliares', payload);
      onClienteCreado({ ...payload, id: res.data.id });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || `Error al guardar el ${tipoAux === 'PROVEEDOR' ? 'proveedor' : 'cliente'}.`);
    }
    setLoading(false);
  };

  const esRuc = form.tipo_documento_identidad_id === '6';
  const esDni = form.tipo_documento_identidad_id === '1';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <PersonAddIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          {tipoAux === 'PROVEEDOR' ? 'Registrar nuevo proveedor' : 'Registrar nuevo cliente'}
        </Typography>
        <IconButton size="small" onClick={onClose} disabled={loading}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Grid container spacing={2}>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>
            </Grid>
          )}
          {infoConsulta && (
            <Grid item xs={12}>
              <Alert
                severity={infoConsulta.startsWith('✓') ? 'success' : 'info'}
                sx={{ py: 0.5 }}
              >
                {infoConsulta}
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} sm={4}>
            <TextField
              select fullWidth size="small"
              label="Tipo de documento"
              name="tipo_documento_identidad_id"
              value={form.tipo_documento_identidad_id}
              onChange={handleChange}
            >
              {TIPOS_DOC.map(t => (
                <MenuItem key={t.codigo} value={t.codigo}>{t.descripcion}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth size="small" required
              label={esRuc ? 'RUC (11 dígitos)' : esDni ? 'DNI (8 dígitos)' : 'Número de documento'}
              name="numero_documento"
              value={form.numero_documento}
              onChange={handleChange}
              inputProps={{ maxLength: esRuc ? 11 : esDni ? 8 : 20 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {consultando
                      ? <CircularProgress size={18} />
                      : (
                        <IconButton
                          size="small"
                          title={'Consultar ' + (esRuc ? 'SUNAT' : 'RENIEC')}
                          onClick={() => consultarDocumento()}
                        >
                          <SearchIcon fontSize="small" />
                        </IconButton>
                      )
                    }
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth size="small" required
              label={esRuc ? 'Razón Social' : 'Razón Social / Apellidos y Nombres'}
              name="razon_social"
              value={form.razon_social}
              onChange={handleChange}
            />
          </Grid>

          {esDni && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small"
                  label="Nombres"
                  name="nombres"
                  value={form.nombres}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth size="small"
                  label="Apellidos"
                  name="apellidos"
                  value={form.apellidos}
                  onChange={handleChange}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth size="small"
              label="Nombre Comercial"
              name="nombre_comercial"
              value={form.nombre_comercial}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth size="small"
              label="Dirección"
              name="direccion"
              value={form.direccion}
              onChange={handleChange}
            />
          </Grid>

          {/* Ubigeo */}
          <Grid item xs={12}>
            <Autocomplete
              options={ubigeoOpciones}
              getOptionLabel={o => ubigeoLabel(o)}
              value={ubigeoSeleccionado}
              inputValue={ubigeoInput}
              onInputChange={handleUbigeoInput}
              onChange={(_, v) => {
                setUbigeoSeleccionado(v);
                setUbigeoInput(v ? ubigeoLabel(v) : '');
                setForm(f => ({ ...f, ubigeo: v ? v.codigo : '' }));
              }}
              isOptionEqualToValue={(o, v) => o.codigo === v.codigo}
              noOptionsText={ubigeoInput.length >= 2 ? 'Sin resultados' : 'Escribe para buscar ubigeo...'}
              size="small"
              renderInput={params => (
                <TextField
                  {...params}
                  label="Ubigeo (departamento / provincia / distrito) *"
                  size="small"
                  fullWidth
                  error={!form.ubigeo}
                  helperText={!form.ubigeo ? 'Requerido para emisión electrónica' : 'Por defecto: Lima. Escribe para cambiar.'}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small"
              label="Teléfono"
              name="telefono"
              value={form.telefono}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small"
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
            />
          </Grid>

          {/* Condición de pago */}
          <Grid item xs={12}>
            <Autocomplete
              options={condicionesPago}
              getOptionLabel={o => o.descripcion || ''}
              value={condSeleccionada}
              onChange={(_, v) => {
                setCondSeleccionada(v);
                setForm(f => ({ ...f, condicion_pago: v ? v.id : '' }));
              }}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              size="small"
              renderInput={params => (
                <TextField
                  {...params}
                  label="Condición de pago"
                  size="small"
                  fullWidth
                  helperText="Por defecto: Contado"
                />
              )}
            />
          </Grid>

        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary">* Campos obligatorios</Typography>
        </Box>
        <Button
          onClick={onClose}
          disabled={loading}
          startIcon={<CloseIcon />}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {loading ? 'Guardando...' : tipoAux === 'PROVEEDOR' ? 'Guardar Proveedor' : 'Guardar Cliente'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

